/**
 * Matematika murni varian liquid-morph (tanpa DOM/GPU, diuji langsung):
 * fase siklus tenang → kusut → tetesan → tenang, putaran, kualitas raymarch
 * adaptif, dan geometri sprite. Angka-angkanya dipindahkan SETIA dari prototipe
 * putaran 2 (_refs/jenna-orb/prototype/orb-liquid-morph-prototype.html).
 */

import {
	ORB_MORPH_MEDIUM_MAX_DEVICE_PX,
	ORB_MORPH_SMALL_MAX_DEVICE_PX,
	ORB_MORPH_STEPS,
} from "@/components/assistant/orb/orb.config";
import type { WebglOrbRect } from "./orb-webgl-stage.types";

/** Satu siklus penuh (detik). */
export const MORPH_CYCLE_S = 9;
/** Waktu animasi frame TENANG (bola) saat beku/reduced-motion; fase siklusnya masih tenang. */
export const MORPH_CALM_TIME_S = 1.5;

/** Fase siklus (0–1): kusut naik di [START, PEAK], bertahan sampai RELEASE, turun sampai END. */
const ACTIVE_START = 0.2;
const ACTIVE_PEAK = 0.5;
const ACTIVE_RELEASE = 0.62;
const ACTIVE_END = 0.95;
/** Tetesan (puncak runcing): puncak Gauss di fase ini, selebar WIDTH, setinggi TAPER. */
const DROP_CENTER = 0.7;
const DROP_WIDTH = 0.09;
const DROP_TAPER = 0.62;

/** Putaran pelan orb: kecepatan (rad/dtk) dan sumbu miringnya. */
const ROTATION_RATE_RAD_S = 0.28;
const ROTATION_AXIS = [0.4, 1.0, 0.25] as const;

/**
 * Tema terang (perlakuan B): tetap transparan; hanya tint badan SANGAT tipis
 * supaya orb tidak lenyap di putih. Alpha dibatasi di atas supaya tak pernah
 * menjadi badan solid.
 */
export const MORPH_LIGHT_TINT_ALPHA = 0.05;
export const MORPH_LIGHT_TINT_ALPHA_MAX = 0.1;
export const MORPH_LIGHT_TINT_RGB = [0.3, 0.26, 0.62] as const;

export interface MorphCycleState {
	/** 0 = tenang (bola), 1 = kusut penuh. */
	k: number;
	/** 0 = bola, >0 = puncak tetesan. */
	taper: number;
}

export interface MorphFrameState extends MorphCycleState {
	/** Waktu animasi (detik) yang dikirim ke shader sebagai uTime. */
	time: number;
}

const smoothstep = (a: number, b: number, x: number): number => {
	const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
	return t * t * (3 - 2 * t);
};

/** Fase 0–1 dalam siklus; aman untuk waktu negatif dan NaN (jatuh ke 0). */
export function morphCyclePhase(timeS: number): number {
	if (!Number.isFinite(timeS)) return 0;
	return (
		(((timeS % MORPH_CYCLE_S) + MORPH_CYCLE_S) % MORPH_CYCLE_S) / MORPH_CYCLE_S
	);
}

/** Tingkat kusut & taper pada waktu t. Periodik: nilai di t dan t + siklus sama. */
export function morphCycleState(timeS: number): MorphCycleState {
	const u = morphCyclePhase(timeS);
	const k =
		smoothstep(ACTIVE_START, ACTIVE_PEAK, u) *
		(1 - smoothstep(ACTIVE_RELEASE, ACTIVE_END, u));
	const taper = DROP_TAPER * Math.exp(-(((u - DROP_CENTER) / DROP_WIDTH) ** 2));
	return { k, taper };
}

/**
 * Keadaan frame. `still` (gerak dimatikan atau idle beku) = bola TENANG pada
 * waktu tetap — bukan pose kusut yang kebetulan membeku di tengah siklus.
 */
export function morphFrameState(
	elapsedS: number,
	still: boolean,
): MorphFrameState {
	if (still) return { time: MORPH_CALM_TIME_S, k: 0, taper: 0 };
	return { time: elapsedS, ...morphCycleState(elapsedS) };
}

/** Matriks 3×3 kolom-mayor (untuk uniformMatrix3fv): koordinat tampilan → objek. */
export function morphRotation(timeS: number): Float32Array {
	const a = -ROTATION_RATE_RAD_S * timeS;
	const len = Math.hypot(...ROTATION_AXIS);
	const [x, y, z] = ROTATION_AXIS.map((v) => v / len) as [
		number,
		number,
		number,
	];
	const c = Math.cos(a);
	const s = Math.sin(a);
	const C = 1 - c;
	const m = [
		c + x * x * C,
		x * y * C - z * s,
		x * z * C + y * s,
		y * x * C + z * s,
		c + y * y * C,
		y * z * C - x * s,
		z * x * C - y * s,
		z * y * C + x * s,
		c + z * z * C,
	] as const;
	return new Float32Array([
		m[0],
		m[3],
		m[6],
		m[1],
		m[4],
		m[7],
		m[2],
		m[5],
		m[8],
	]);
}

/** Langkah raymarch menurut sisi sprite dalam piksel perangkat (kualitas adaptif). */
export function morphSteps(devicePx: number): number {
	if (devicePx <= ORB_MORPH_SMALL_MAX_DEVICE_PX) return ORB_MORPH_STEPS.small;
	if (devicePx <= ORB_MORPH_MEDIUM_MAX_DEVICE_PX) return ORB_MORPH_STEPS.medium;
	return ORB_MORPH_STEPS.large;
}

/** Perbesar rect di sekitar titik tengahnya (sprite lebih besar dari placeholder). */
export function scaleRectAroundCenter(
	rect: WebglOrbRect,
	scale: number,
): WebglOrbRect {
	const width = rect.width * scale;
	const height = rect.height * scale;
	return {
		x: rect.x + (rect.width - width) / 2,
		y: rect.y + (rect.height - height) / 2,
		width,
		height,
	};
}
